import apiService from './api.service';
import {
  AdminOverview,
  AdminUserListResponse,
  AdminUserListParams,
  AdminUserDetail,
  AdminUserUpdate,
  AdminUserCreate,
  AdminSystemHealth,
  AdminRevenue,
  AdminAnalysisListResponse,
  AdminAnalysisListParams,
  AdminSFTPInfo,
  AdminChangeTier,
  AdminUsageBreakdown,
  AdminIngestionSourceItem,
  AdminAuditLogResponse,
  AdminTimeline,
  AdminRetryResponse,
  AdminUserPricingResponse,
  AdminCustomPricingCreate,
  AnalysisCostDetail,
  ActualCostResponse,
  MarginResponse,
  PerCallCostStatsResponse,
} from '../types/admin.types';
import {
  RuleCatalogResponse,
  AnalysisConfigurationResponse,
  AnalysisConfigurationList,
  AnalysisConfigurationCreatePayload,
  AnalysisConfigurationUpdatePayload,
} from '../types/analysis-config.types';
import {
  CustomRuleResponse,
  CustomRuleList,
  CustomRuleCreatePayload,
  CustomRuleUpdatePayload,
} from '../types/custom-rule.types';
import { AnalysisDetail } from '../types/analysis.types';

class AdminService {
  async getOverview(): Promise<AdminOverview> {
    const response = await apiService.client.get<AdminOverview>('/admin/overview');
    return response.data;
  }

  async listUsers(params: AdminUserListParams = {}): Promise<AdminUserListResponse> {
    const response = await apiService.client.get<AdminUserListResponse>('/admin/users', {
      params,
    });
    return response.data;
  }

  async getUserDetail(userId: string): Promise<AdminUserDetail> {
    const response = await apiService.client.get<AdminUserDetail>(`/admin/users/${userId}`);
    return response.data;
  }

  async updateUser(userId: string, updates: AdminUserUpdate): Promise<AdminUserDetail> {
    const response = await apiService.client.patch<AdminUserDetail>(
      `/admin/users/${userId}`,
      updates
    );
    return response.data;
  }

  async createUser(data: AdminUserCreate): Promise<AdminUserDetail> {
    const response = await apiService.client.post<AdminUserDetail>('/admin/users', data);
    return response.data;
  }

  async getSystemHealth(): Promise<AdminSystemHealth> {
    const response = await apiService.client.get<AdminSystemHealth>('/admin/system/health');
    return response.data;
  }

  async getRevenue(): Promise<AdminRevenue> {
    const response = await apiService.client.get<AdminRevenue>('/admin/revenue');
    return response.data;
  }

  async listAllAnalyses(params: AdminAnalysisListParams = {}): Promise<AdminAnalysisListResponse> {
    const response = await apiService.client.get<AdminAnalysisListResponse>('/admin/analyses', {
      params,
    });
    return response.data;
  }

  async listUserAnalyses(userId: string, params: AdminAnalysisListParams = {}): Promise<AdminAnalysisListResponse> {
    const response = await apiService.client.get<AdminAnalysisListResponse>(
      `/admin/users/${userId}/analyses`,
      { params },
    );
    return response.data;
  }

  async getUserAnalysis(userId: string, analysisId: string): Promise<AnalysisDetail> {
    const response = await apiService.client.get<AnalysisDetail>(
      `/admin/users/${userId}/analyses/${analysisId}`,
    );
    return response.data;
  }

  // Per-Analysis Cost
  async getAnalysisCost(userId: string, analysisId: string): Promise<AnalysisCostDetail> {
    const response = await apiService.client.get<AnalysisCostDetail>(
      `/admin/users/${userId}/analyses/${analysisId}/cost`,
    );
    return response.data;
  }

  // Password Reset
  async sendPasswordReset(userId: string): Promise<{ message: string }> {
    const response = await apiService.client.post(`/admin/users/${userId}/send-password-reset`);
    return response.data;
  }

  // SFTP Management
  async getSFTPInfo(userId: string): Promise<AdminSFTPInfo> {
    const response = await apiService.client.get<AdminSFTPInfo>(`/admin/users/${userId}/sftp`);
    return response.data;
  }

  async retrySFTPProvisioning(userId: string): Promise<{ message: string }> {
    const response = await apiService.client.post(`/admin/users/${userId}/sftp/retry`);
    return response.data;
  }

  async resetSFTPPassword(userId: string): Promise<{ message: string }> {
    const response = await apiService.client.post(`/admin/users/${userId}/sftp/reset-password`);
    return response.data;
  }

  // Custom Pricing
  async getUserPricing(userId: string): Promise<AdminUserPricingResponse> {
    const response = await apiService.client.get<AdminUserPricingResponse>(
      `/admin/users/${userId}/pricing`,
    );
    return response.data;
  }

  async createUserPricing(userId: string, data: AdminCustomPricingCreate): Promise<AdminUserPricingResponse> {
    const response = await apiService.client.post<AdminUserPricingResponse>(
      `/admin/users/${userId}/pricing`,
      data,
    );
    return response.data;
  }

  async updateUserPricing(userId: string, data: AdminCustomPricingCreate): Promise<AdminUserPricingResponse> {
    const response = await apiService.client.patch<AdminUserPricingResponse>(
      `/admin/users/${userId}/pricing`,
      data,
    );
    return response.data;
  }

  async deleteUserPricing(userId: string): Promise<AdminUserPricingResponse> {
    const response = await apiService.client.delete<AdminUserPricingResponse>(
      `/admin/users/${userId}/pricing`,
    );
    return response.data;
  }

  // Subscription Tier Change
  async changeSubscriptionTier(userId: string, data: AdminChangeTier): Promise<{ message: string }> {
    const response = await apiService.client.post(
      `/admin/users/${userId}/subscription/change-tier`,
      data,
    );
    return response.data;
  }

  // Usage Breakdown
  async getUsageBreakdown(userId: string): Promise<AdminUsageBreakdown> {
    const response = await apiService.client.get<AdminUsageBreakdown>(
      `/admin/users/${userId}/usage-breakdown`,
    );
    return response.data;
  }

  // Ingestion Sources
  async getUserIngestionSources(userId: string): Promise<AdminIngestionSourceItem[]> {
    const response = await apiService.client.get<AdminIngestionSourceItem[]>(
      `/admin/users/${userId}/ingestion-sources`,
    );
    return response.data;
  }

  async updateIngestionSource(
    userId: string,
    sourceId: string,
    status: string,
  ): Promise<{ message: string }> {
    const response = await apiService.client.patch(
      `/admin/users/${userId}/ingestion-sources/${sourceId}`,
      { status },
    );
    return response.data;
  }

  // Audit Log
  async getAuditLog(params: {
    limit?: number;
    offset?: number;
    action?: string;
  } = {}): Promise<AdminAuditLogResponse> {
    const response = await apiService.client.get<AdminAuditLogResponse>('/admin/audit-log', {
      params,
    });
    return response.data;
  }

  // Customer Timeline
  async getUserTimeline(userId: string): Promise<AdminTimeline> {
    const response = await apiService.client.get<AdminTimeline>(
      `/admin/users/${userId}/timeline`,
    );
    return response.data;
  }

  // Analysis Retry
  async retryAnalysis(analysisId: string): Promise<AdminRetryResponse> {
    const response = await apiService.client.post<AdminRetryResponse>(
      `/admin/analyses/${analysisId}/retry`,
    );
    return response.data;
  }

  async retryFailedAnalyses(sinceHours: number = 24): Promise<AdminRetryResponse> {
    const response = await apiService.client.post<AdminRetryResponse>(
      '/admin/analyses/retry-failed',
      null,
      { params: { since_hours: sinceHours } },
    );
    return response.data;
  }

  async unstickProcessing(): Promise<AdminRetryResponse> {
    const response = await apiService.client.post<AdminRetryResponse>(
      '/admin/analyses/unstick-processing',
    );
    return response.data;
  }

  // Cost-to-Serve
  async getActualCosts(
    userId: string,
    period: string = 'last_30d',
  ): Promise<ActualCostResponse> {
    const response = await apiService.client.get<ActualCostResponse>(
      `/admin/users/${userId}/cost-to-serve/actual`,
      { params: { period } },
    );
    return response.data;
  }

  async getCustomerMargin(
    userId: string,
    period: string = 'last_30d',
  ): Promise<MarginResponse> {
    const response = await apiService.client.get<MarginResponse>(
      `/admin/users/${userId}/cost-to-serve/margin`,
      { params: { period } },
    );
    return response.data;
  }

  async getPerCallCostStats(
    userId: string,
    range: { from: string; to: string },
  ): Promise<PerCallCostStatsResponse> {
    const response = await apiService.client.get<PerCallCostStatsResponse>(
      `/admin/users/${userId}/cost-to-serve/per-call-stats`,
      { params: { from: range.from, to: range.to } },
    );
    return response.data;
  }

  // -------- Rule catalog + per-customer configuration (Phase C) --------

  async getRuleCatalog(): Promise<RuleCatalogResponse> {
    const response = await apiService.client.get<RuleCatalogResponse>(
      '/admin/analysis-config/catalog',
    );
    return response.data;
  }

  async getDefaultAnalysisConfig(userId: string): Promise<AnalysisConfigurationResponse> {
    const response = await apiService.client.get<AnalysisConfigurationResponse>(
      `/admin/analysis-config/${userId}/default`,
    );
    return response.data;
  }

  async listAnalysisConfigs(userId: string): Promise<AnalysisConfigurationList> {
    const response = await apiService.client.get<AnalysisConfigurationList>(
      `/admin/analysis-config/${userId}`,
    );
    return response.data;
  }

  async createAnalysisConfig(
    userId: string,
    payload: AnalysisConfigurationCreatePayload,
  ): Promise<AnalysisConfigurationResponse> {
    const response = await apiService.client.post<AnalysisConfigurationResponse>(
      `/admin/analysis-config/${userId}`,
      payload,
    );
    return response.data;
  }

  async updateAnalysisConfig(
    userId: string,
    configId: string,
    payload: AnalysisConfigurationUpdatePayload,
  ): Promise<AnalysisConfigurationResponse> {
    const response = await apiService.client.put<AnalysisConfigurationResponse>(
      `/admin/analysis-config/${userId}/${configId}`,
      payload,
    );
    return response.data;
  }

  // -------- Phase F: Custom rules --------

  async listCustomRules(userId: string): Promise<CustomRuleList> {
    const response = await apiService.client.get<CustomRuleList>(
      `/admin/users/${userId}/custom-rules`,
    );
    return response.data;
  }

  async createCustomRule(
    userId: string,
    payload: CustomRuleCreatePayload,
  ): Promise<CustomRuleResponse> {
    const response = await apiService.client.post<CustomRuleResponse>(
      `/admin/users/${userId}/custom-rules`,
      payload,
    );
    return response.data;
  }

  async updateCustomRule(
    userId: string,
    ruleId: string,
    payload: CustomRuleUpdatePayload,
  ): Promise<CustomRuleResponse> {
    const response = await apiService.client.patch<CustomRuleResponse>(
      `/admin/users/${userId}/custom-rules/${ruleId}`,
      payload,
    );
    return response.data;
  }

  async deleteCustomRule(userId: string, ruleId: string): Promise<void> {
    await apiService.client.delete(`/admin/users/${userId}/custom-rules/${ruleId}`);
  }
}

export default new AdminService();
